import { Input } from "../ui/Input";
import { UserSelector } from "./UserSelector";
import { PricePointSelector } from "./PricePointSelector";
import type { AccountUser } from "../../lib/hooks/useAccountUsers";
import type { EventPricePoint } from "../../lib/types";

interface ParticipantFormSectionProps {
  index: number;
  quantity: number;
  firstName: string;
  lastName: string;
  selectedUserId: string | "other";
  selectedPricePointId: string;
  allAccountUsers: AccountUser[];
  selectedUserIds: (string | "other")[];
  availablePricePoints: EventPricePoint[];
  /** In-window tiers for the category dropdown (includes full as disabled options). */
  categoryPricePoints: EventPricePoint[];
  bookingOpen: boolean;
  onFirstNameChange: (index: number, value: string) => void;
  onLastNameChange: (index: number, value: string) => void;
  onUserChange: (index: number, value: string | "other") => void;
  onPricePointChange: (index: number, value: string) => void;
  errors?: {
    firstName?: { message?: string };
    lastName?: { message?: string };
    pricePointId?: { message?: string };
  };
}

/**
 * Component for a single participant's form fields (name, user selection, price point)
 */
export function ParticipantFormSection({
  index,
  quantity,
  firstName,
  lastName,
  selectedUserId,
  selectedPricePointId,
  allAccountUsers,
  selectedUserIds,
  availablePricePoints,
  categoryPricePoints,
  bookingOpen,
  onFirstNameChange,
  onLastNameChange,
  onUserChange,
  onPricePointChange,
  errors,
}: ParticipantFormSectionProps) {
  const availableUsers = allAccountUsers.filter((u) => {
    const isAlreadySelected = selectedUserIds.some(
      (selectedId, i) => i !== index && selectedId === u.id,
    );
    return !isAlreadySelected;
  });

  const isOther = selectedUserId === "other";

  return (
    <div className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-100">
      <UserSelector
        value={selectedUserId}
        availableUsers={availableUsers}
        allAccountUsers={allAccountUsers}
        selectedUserIds={selectedUserIds}
        index={index}
        onChange={(value) => onUserChange(index, value)}
        disabled={!bookingOpen || availablePricePoints.length === 0}
      />

      {isOther && (
        <div className="grid grid-cols-2 gap-2">
          <Input
            label={
              quantity === 1
                ? "First Name"
                : `Participant ${index + 1} First Name`
            }
            placeholder="Enter first name"
            value={firstName}
            onChange={(e) => onFirstNameChange(index, e.target.value)}
            disabled={!bookingOpen || availablePricePoints.length === 0}
            required
            error={errors?.firstName?.message}
          />
          <Input
            label={
              quantity === 1 ? "Last Name" : `Participant ${index + 1} Last Name`
            }
            placeholder="Enter last name"
            value={lastName}
            onChange={(e) => onLastNameChange(index, e.target.value)}
            disabled={!bookingOpen || availablePricePoints.length === 0}
            required
            error={errors?.lastName?.message}
          />
        </div>
      )}

      <PricePointSelector
        value={selectedPricePointId}
        categoryPricePoints={categoryPricePoints}
        onChange={(value) => onPricePointChange(index, value)}
        disabled={!bookingOpen}
        required
        error={errors?.pricePointId?.message}
      />
    </div>
  );
}

